import { useEffect, useMemo, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';
import { HelperText, Label } from '../../../components/UiComponents/Ui';
import MultiSelectDropdown from '../../../components/MultiSelectDropdown';
import { useQuery } from '@tanstack/react-query';
import { surveySupplierAllocationList } from '../../../Actions/surveyAllocationActions';
import { useGlobalContext } from '../../../Contexts/GlobalContext';
import { useState } from 'react';

const EMPTY = [];

const SelectSuppliers = ({
  dropdownType = 'multiple',
  placeholder = 'Select Supplier',
  setURLParams = false,
  fieldKey = 'suppliers',
  selected = EMPTY,
  setSelected,
  width = '200px',
  showFooter = true,
  isSuveySupplier = false,
  surveyId,
}) => {
  const { user } = useGlobalContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialSuppliers =
    setURLParams && searchParams.get(fieldKey)
      ? searchParams
          .get(fieldKey)
          ?.split(',')
          ?.map((d) => ({
            label: decodeURIComponent(d) || '',
            value: decodeURIComponent(d) || '',
          }))
      : [];

  const [supplierSelect, setSupplierSelect] = useState(initialSuppliers || []);

  const isMultiple = dropdownType === 'multiple';
  const hydrated = useRef(false);

  const orgId = user?.organization?._id;

  const queryKey = isSuveySupplier
    ? ['survey-suppliers-list', surveyId, orgId]
    : ['active-suppliers-list'];

  const {
    data: suppliersList,
    isLoading: suppliersListLoading,
    error: suppliersListError,
  } = useQuery({
    queryKey: queryKey,
    queryFn: async () =>
      isSuveySupplier
        ? surveySupplierAllocationList({
            surveyId: surveyId ?? null,
            orgId,
          })
        : () => {},
  });

  //   console.log({
  //     suppliersList,
  //     suppliersListLoading,
  //     suppliersListError,
  //   });

  const options = useMemo(
    () =>
      Array.isArray(suppliersList?.data)
        ? suppliersList.data.filter(
            (item) => Boolean(item?.value) && Boolean(item?.label),
          )
        : [],
    [suppliersList?.data],
  );

  useEffect(() => {
    if (hydrated.current || !options.length) return;
    hydrated.current = true;
    if (selected.length) return;

    const raw = searchParams?.get(fieldKey);
    if (!raw) return;

    const matched = raw
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
      .map((v) => options.find((o) => o.value === v || o.label === v))
      .filter(Boolean);

    if (matched.length) {
      setSelected?.(isMultiple ? matched : matched.slice(0, 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  if (suppliersListLoading) {
    return (
      <div role="status">
        <Label className="cursor-not-allowed">Supplier List Loading...</Label>
      </div>
    );
  }

  if (suppliersListError) {
    return <HelperText>Unable to load supplier list.</HelperText>;
  }

  if (!options.length) {
    return (
      <Label className="cursor-not-allowed">No active suppliers found.</Label>
    );
  }

  return (
    <div style={width ? { width } : undefined}>
      <MultiSelectDropdown
        search
        multiple={isMultiple}
        width={width}
        options={options}
        selected={!isMultiple ? selected : supplierSelect}
        setSelected={(e) => {
          const value = e;
          if (!isMultiple || !showFooter) {
            setSelected(value);
          } else {
            setSupplierSelect(value);
          }
        }}
        dropdownText={placeholder}
        dropdownFooter={isMultiple && showFooter}
        onApply={() => {
          setSelected?.(
            isMultiple ? supplierSelect : supplierSelect.slice(0, 1),
          );
          if (setURLParams) {
            setSearchParams({
              [fieldKey]: supplierSelect
                .map((v) => encodeURIComponent(v.value || v.label))
                .join(','),
            });
          }
        }}
      />
    </div>
  );
};

export default SelectSuppliers;
